// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartContext, withCart } from "@insite/client-framework/Components/CartContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import {
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderApprovalDetailsActionButtons from "@insite/content-library/Widgets/OrderApprovalDetails/OrderApprovalDetailsActionButtons";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@steiner/Overrides/Components/CartTotalDisplay";
import { StyledHr } from "@steiner/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const promotions = getPromotionsDataView(state, state.pages.orderApprovalDetails.cartId).value;

    return {
        orderPromotions: promotions ? getOrderPromotions(promotions) : undefined,
        shippingPromotions: promotions ? getShippingPromotions(promotions) : undefined,
        discountTotal: promotions ? getDiscountTotal(promotions) : undefined,
        // XNG-Changes
        account: getCurrentAccountState(state)?.value,
    };
};

const enum fields {
    isOnlyPrint = "isOnlyPrint",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.isOnlyPrint]?: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartContext;

export interface OrderApprovalDetailsTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    container?: InjectableCss;
    // xng-change
    fullWidthGridItem?: GridItemProps;
    styledHr?: InjectableCss;
    actionButtonsWrapper?: GridItemProps;
}

export const orderApprovalDetailsTotalStyles: OrderApprovalDetailsTotalStyles = {};

const styles = orderApprovalDetailsTotalStyles;

const OrderApprovalDetailsTotal = ({
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    fields,
    // XNG-Changes
    account,
}: Props) => {
    if (!cart) {
        return null;
    }

    return (
        // xng-change - Adding wrapper for styling
        <StyledWrapper {...styles.container} {...{ isOnlyPrint: fields.isOnlyPrint }}>
            <CartTotalDisplay
                cart={cart}
                orderPromotions={orderPromotions}
                shippingPromotions={shippingPromotions}
                discountTotal={discountTotal}
                extendedStyles={styles.cartTotal}
            >
                {/* XNG-Changes */}
                {account?.canApproveOrders && (
                    <>
                        <GridItem {...styles.fullWidthGridItem}>
                            <StyledHr {...styles.styledHr} />
                        </GridItem>
                        <GridItem {...styles.actionButtonsWrapper}>
                            <OrderApprovalDetailsActionButtons
                                cart={cart}
                                {...{
                                    showApproveOrderOnly: true,
                                }}
                            />
                        </GridItem>
                    </>
                )}
            </CartTotalDisplay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCart(OrderApprovalDetailsTotal)),
    definition: {
        group: "Order Approval Details",
        allowedContexts: ["OrderApprovalDetailsPage"],
        displayName: "Order Approval Total",
        // xng-change - Adding isOnlyPrint
        fieldDefinitions: [
            {
                name: fields.isOnlyPrint,
                displayName: "is Only Print",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
